import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

import * as fromRoot from '../../../../../../../state/app.state';
import { projectDetails } from '../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../core/projects.service';
import {
  ConfirmDialogComponent, ConfirmDialogModel,
} from '../../../../../../../shared/components';
import {
  CreateDailyDiaryComponent,
} from '../../../../site-details-page/tab-screens/daily-diary/create-daily-diary/create-daily-diary.component';
import {
  DIARY_DATE_PARAM, DIARY_ID_PARAM, DIARY_SECTION_PARAM, DIARY_SITE_PARAM,
  DiaryViewMode, resolveDiaryDetailDate, resolveDiaryViewMode,
} from '../../../../site-details-page/tab-screens/daily-diary/daily-diary-mode.util';
import {
  resolveDiaryDateWindow, resolveDiaryStatuses,
} from '../../../../site-details-page/tab-screens/daily-diary/daily-diary-filters.util';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  locked: 'Locked',
};

/**
 * Project-level Diary tab.
 *
 * Diaries were only reachable by opening a site and then its Daily diary tab,
 * which meant drilling through a daily report to get to one. This lists every
 * diary on the project in one place; opening one hands off to the site's diary
 * tab in detail mode, so there is a single implementation of the record view.
 *
 * The api only lists diaries per site (`GET /daily-diary/:siteId`), so this
 * fans out over the project's sites and merges. Projects here carry very few
 * sites — usually one — so that stays a single request in practice.
 */
@Component({
  selector: 'app-project-diaries-wrapper',
  templateUrl: './project-diaries-wrapper.component.html',
  styleUrls: ['./project-diaries-wrapper.component.scss'],
  standalone: false,
})
export class ProjectDiariesWrapperComponent implements OnInit, OnDestroy {
  diaries: any[] = [];
  loading = false;
  projectId = '';

  /** 'list' = the project-wide card grid, 'detail' = one day's record. */
  mode: DiaryViewMode = 'list';
  /** The site the open record belongs to; the list spans every site. */
  detailSiteId = '';
  /** Anchor for the horizontal strip. Only meaningful in detail mode. */
  date = moment().format('YYYY-MM-DD');
  triggerDateChange = 0;

  statusLabels = STATUS_LABELS;

  // Driven by the `app-daily-diary-filters` chips on the tab row.
  private startDate = '';
  private endDate = '';
  private selectedStatuses: string[] = [];

  private sites: any[] = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('projectId')
      || this.router.url.split('?')[0].split('/')[2];

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.mode = resolveDiaryViewMode(qp);
        const detailDate = resolveDiaryDetailDate(qp);
        if (detailDate) this.date = detailDate;
        this.detailSiteId = qp[DIARY_SITE_PARAM] || this.detailSiteId;

        // Same resolver the chips use, so the two can never disagree about
        // what "no params" means (defaults: every status, last two weeks).
        const { startDate, endDate } = resolveDiaryDateWindow(qp);
        const statuses = resolveDiaryStatuses(qp);
        const changed = startDate !== this.startDate
          || endDate !== this.endDate
          || statuses.join(',') !== this.selectedStatuses.join(',');
        this.startDate = startDate;
        this.endDate = endDate;
        this.selectedStatuses = statuses;
        if (changed && this.sites.length) this.fetchDiaries();
      });

    // The floating + opens the create dialog; in detail mode it drops the
    // component menu instead (handled by projects-wrapper).
    this.projectsService.createDailyDiary
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.createDiary());

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((project: any) => {
        const sites = project?.sites || [];
        const changed = sites.map((s: any) => s.id).join(',')
          !== this.sites.map((s: any) => s.id).join(',');
        this.sites = sites;
        if (changed && sites.length) this.fetchDiaries();
      });

    this.projectsService.dailyDiaryChanged
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetchDiaries());
  }

  fetchDiaries(): void {
    if (!this.sites.length) return;
    this.loading = true;

    forkJoin(
      this.sites.map((site: any) => this.projectsService
        .fetchDailyDiaries({ siteId: site.id, startDate: this.startDate, endDate: this.endDate })
        // One unreadable site must not blank the whole tab.
        .pipe(catchError(() => of({ data: [] })))),
    ).pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (responses: any[]) => {
          this.loading = false;
          this.diaries = responses
            .flatMap((resp: any, index: number) => (resp?.data || []).map((row: any) => ({
              ...row,
              ...(row.attributes || {}),
              siteId: this.sites[index]?.id,
              siteName: this.sites[index]?.name,
            })))
            .filter((row: any) => !this.selectedStatuses.length
              || this.selectedStatuses.includes(row.status))
            .sort((a, b) => String(b.diary_date).localeCompare(String(a.diary_date)));
        },
        error: () => { this.loading = false; },
      });
  }

  /**
   * Diaries grouped per site, the same shape the Daily reports tab renders
   * (`app-site-reports-panel`): a "<site> (N diaries)" heading over a card
   * grid that collapses to one row with a See more toggle.
   */
  get groups(): { siteId: string; siteName: string; rows: any[] }[] {
    return this.sites
      .map((site: any) => ({
        siteId: site.id,
        siteName: site.name,
        rows: this.diaries.filter(d => d.siteId === site.id),
      }))
      .filter(group => group.rows.length);
  }

  /** Site ids whose grid is expanded past the first row. */
  expanded: Record<string, boolean> = {};

  toggleExpanded(siteId: string): void {
    this.expanded[siteId] = !this.expanded[siteId];
  }

  remarkCount(diary: any): number {
    return diary?.remarks_count ?? diary?.remarks?.length ?? 0;
  }

  /**
   * Hand off to the site's Daily diary tab in detail mode — the same URL the
   * diary list there produces, so the record renders through one code path.
   */
  openDiary(diary: any): void {
    const siteId = diary?.siteId || diary?.site?.id;
    if (!siteId) return;
    this.detailSiteId = siteId;
    this.router.navigate([], {
      queryParams: {
        [DIARY_SITE_PARAM]: siteId,
        [DIARY_DATE_PARAM]: moment(diary.diary_date).format('YYYY-MM-DD'),
        [DIARY_ID_PARAM]: diary.id || diary.uuid,
        [DIARY_SECTION_PARAM]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  createDiary(): void {
    const site = this.sites[0];
    if (!site || this.mode === 'detail') return;
    this.dialog.open(CreateDailyDiaryComponent, {
      disableClose: false,
      panelClass: 'floating-dialog-box',
      data: { projectId: this.projectId, siteId: site.id },
    });
  }

  /** Back to Mode A. */
  backToList(): void {
    this.projectsService.closeRightPanel.next();
    this.router.navigate([], {
      queryParams: {
        [DIARY_SITE_PARAM]: null,
        [DIARY_DATE_PARAM]: null,
        [DIARY_ID_PARAM]: null,
        [DIARY_SECTION_PARAM]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Same guard the daily report's strip uses: confirm before discarding, and
   * bounce the strip back to the old day if the user cancels.
   */
  handleDateChange(date: string): void {
    const selected = moment(date).format('YYYY-MM-DD');
    if (!this.projectsService.isAnyFormDirty.getValue()) {
      this.applyDate(selected);
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Unsaved changes',
        `There are unsaved changes.<br/>Are you sure you want to discard?`,
      ),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.projectsService.closeRightPanel.next();
        this.applyDate(selected).then(() => this.projectsService.isAnyFormDirty.next(false));
      } else {
        this.triggerDateChange = new Date().getTime();
      }
    });
  }

  private applyDate(date: string) {
    return this.router.navigate([], {
      queryParams: {
        [DIARY_DATE_PARAM]: date,
        // A different day means a different set of diaries.
        [DIARY_ID_PARAM]: null,
        [DIARY_SECTION_PARAM]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
